import { useQuery } from '@tanstack/vue-query'
import type { BlogDetail } from '@/types'
import { queryKeys } from './keys'
import {
  useBikesCategoriesQuery,
  useCategoriesQuery,
  useProductsQuery
} from './useCatalogQueries'

interface ArticlesListResponse {
  data?: BlogDetail[]
}

export async function useHomePageQueries() {
  const { locale } = useI18n()
  const { products, productsQuery } = useProductsQuery()
  const { categories, categoriesQuery } = useCategoriesQuery()
  const { bikesCategories, bikesQuery } = useBikesCategoriesQuery()

  const articlesQuery = useQuery({
    queryKey: computed(() => queryKeys.homeArticles(locale.value)),
    queryFn: async () => {
      try {
        const response = await apiGet<ArticlesListResponse>(
          `/articles?lang=${locale.value}`
        )
        const allArticles = response?.data ?? []
        return allArticles.slice(-3)
      } catch {
        return []
      }
    },
    enabled: computed(() => !!locale.value),
    placeholderData: [] as BlogDetail[]
  })

  await Promise.all([
    productsQuery.suspense(),
    categoriesQuery.suspense(),
    bikesQuery.suspense(),
    articlesQuery.suspense()
  ])

  const homeArticles = computed(() => articlesQuery.data.value ?? [])

  const isPending = computed(
    () =>
      productsQuery.isPending.value ||
      categoriesQuery.isPending.value ||
      bikesQuery.isPending.value ||
      articlesQuery.isPending.value
  )

  return {
    products,
    categories,
    bikesCategories,
    homeArticles,
    isPending,
    productsQuery,
    categoriesQuery,
    bikesQuery,
    articlesQuery
  }
}
